/**
 * Parse website /inquire lead notes (labeled lines from the public form).
 */

export type InquireLeadProjectInfo = {
  source?: string;
  readiness?: string;
  message?: string;
  projectType?: string;
  timeline?: string;
  budget?: string;
  budgetAmount?: number;
  fromInquireForm: boolean;
};

function normalizeLabel(raw: string): string {
  return raw.trim().toLowerCase().replace(/[_-]+/g, ' ');
}

function parseBudgetAmount(raw: string): number | undefined {
  const cleaned = raw.replace(/[^0-9.]/g, '');
  if (!cleaned) return undefined;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Notes from www.lctimberframes.com/inquire often look like:
 *   Source: Website
 *   Readiness: Planning
 *   Message: ...
 * Plus optional Type / Timeline / Budget lines.
 */
export function parseInquireLeadNotes(notes: string | null | undefined): InquireLeadProjectInfo {
  const text = typeof notes === 'string' ? notes : '';
  const info: InquireLeadProjectInfo = { fromInquireForm: false };
  if (!text.trim()) return info;

  const lower = text.toLowerCase();
  if (lower.includes('lctimberframes.com/inquire') || lower.includes('/inquire')) {
    info.fromInquireForm = true;
  }

  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const match = line.match(/^\s*([A-Za-z][A-Za-z _-]{0,40})\s*:\s*(.*)$/);
    if (!match) continue;
    const label = normalizeLabel(match[1]);
    const value = match[2].trim();
    if (!value) continue;
    if (label === 'source') {
      info.source = value;
      if (/inquire/i.test(value) || /lctimberframes\.com\/inquire/i.test(value)) {
        info.fromInquireForm = true;
      }
    } else if (label === 'readiness' || label === 'project readiness') {
      info.readiness = value;
    } else if (label === 'message' || label === 'msg') {
      info.message = value;
    } else if (label === 'type' || label === 'project type' || label === 'projecttype') {
      info.projectType = value;
    } else if (label === 'timeline' || label === 'timeframe') {
      info.timeline = value;
    } else if (label === 'budget') {
      info.budget = value;
      info.budgetAmount = parseBudgetAmount(value);
    }
  }

  // Multi-line Message: capture remaining text after a Message: line when value empty on same line
  if (!info.message) {
    const msgIdx = lines.findIndex((l) => /^\s*message\s*:/i.test(l));
    if (msgIdx >= 0) {
      const same = lines[msgIdx].replace(/^\s*message\s*:\s*/i, '').trim();
      if (same) {
        info.message = same;
      } else {
        const rest: string[] = [];
        for (let i = msgIdx + 1; i < lines.length; i += 1) {
          if (/^\s*[A-Za-z][A-Za-z _-]{0,40}\s*:/.test(lines[i])) break;
          rest.push(lines[i]);
        }
        const joined = rest.join('\n').trim();
        if (joined) info.message = joined;
      }
    }
  }

  return info;
}

export function inquireFormMessageForTeamNote(
  notes: string | null | undefined,
): string | null {
  const parsed = parseInquireLeadNotes(notes);
  const message = (parsed.message || '').trim();
  if (!message) return null;
  if (!parsed.fromInquireForm && !/inquire/i.test(String(notes || ''))) {
    // Still allow when Message line exists with inquire URL elsewhere in notes
    if (!/lctimberframes\.com\/inquire/i.test(String(notes || ''))) return null;
  }
  return message;
}
